import React from 'react';
import { Calendar, CheckCircle, Clock, AlertCircle, FileText, Edit, Trash2 } from 'lucide-react';

const AssignmentCard = ({
  assignment,
  role,
  onEdit,
  onDelete,
  onSubmit,
  onView
}) => {
  const isLecturer = role === 'lecturer' || role === 'admin';
  const isStudent = role === 'student';

  const now = new Date();
  const dueDate = assignment.dueDate ? new Date(assignment.dueDate) : null;
  const isOverdue = dueDate ? now > dueDate : false;
  const isSubmitted = assignment.submitted;

  const getStatusBadge = () => {
    if (assignment.status === 'draft') {
      return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">Draft</span>;
    }

    if (isStudent) {
      if (isSubmitted) {
        if (assignment.submissionStatus === 'graded') {
          return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Graded: {assignment.grade}/{assignment.points}</span>;
        }
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium flex items-center gap-1"><CheckCircle size={12} /> Submitted</span>;
      }
      if (isOverdue) {
        if (assignment.allowLateSubmissions) {
          return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium flex items-center gap-1"><AlertCircle size={12} /> Late</span>;
        }
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium flex items-center gap-1"><AlertCircle size={12} /> Missing</span>;
      }
    }

    if (isOverdue) {
      return <span className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs font-medium">Closed</span>;
    }

    return <span className="px-2 py-1 bg-green-50 text-green-600 rounded text-xs font-medium">Active</span>;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
            {assignment.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            {getStatusBadge()}
            <span className="text-xs text-gray-500">
              {assignment.submissionType === 'both' ? 'File & Text' : assignment.submissionType === 'file' ? 'File Upload' : 'Text Entry'}
            </span>
          </div>
        </div>
        {isLecturer && (
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(assignment)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Edit Assignment"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => onDelete(assignment.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete Assignment"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>

      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {assignment.description || 'No description provided.'}
      </p>

      <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-50">
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-1.5 ${isOverdue && !isSubmitted ? 'text-red-600 font-medium' : ''}`}>
            <Clock size={14} />
            <span>
              {dueDate
                ? `${dueDate.toLocaleDateString()} ${dueDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : 'Flexible Schedule'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <FileText size={14} />
            <span>{assignment.points} pts</span>
          </div>
        </div>

        {isStudent && (
          <button
            onClick={() => onSubmit(assignment)}
            disabled={!assignment.allowLateSubmissions && isOverdue && !isSubmitted}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${isSubmitted
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : (!assignment.allowLateSubmissions && isOverdue)
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
          >
            {isSubmitted ? 'View Submission' : (isOverdue ? 'Submit Late' : 'Start Assignment')}
          </button>
        )}

        {isLecturer && (
          <button
            onClick={() => onView(assignment)}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            View Submissions
          </button>
        )}
      </div>
    </div>
  );
};

export default AssignmentCard;
